<template lang="pug">
    .reservations-list
        //- Stats cards (compact for embedded, full for standalone)
        .stats-compact(v-if="embedded" style="margin-bottom: 15px")
            .stats-compact__item
                span.stats-compact__number {{ stats.total }}
                span.stats-compact__label {{ $t('Total') }}
            .stats-compact__item
                span.stats-compact__number.text-success {{ stats.confirmed }}
                span.stats-compact__label {{ $t('Confirmed') }}
            .stats-compact__item
                span.stats-compact__number.text-warning {{ stats.pending }}
                span.stats-compact__label {{ $t('Pending') }}
            .stats-compact__item
                span.stats-compact__number {{ stats.totalGuests }}
                span.stats-compact__label {{ $t('Guests') }}
        el-row(v-else :gutter="20" style="margin-bottom: 20px")
            el-col(:span="6")
                el-card(shadow="hover")
                    .stat-card
                        .stat-card__number {{ stats.total }}
                        .stat-card__label {{ $t('Total') }}
            el-col(:span="6")
                el-card(shadow="hover")
                    .stat-card
                        .stat-card__number.text-success {{ stats.confirmed }}
                        .stat-card__label {{ $t('Confirmed') }}
            el-col(:span="6")
                el-card(shadow="hover")
                    .stat-card
                        .stat-card__number.text-warning {{ stats.pending }}
                        .stat-card__label {{ $t('Pending') }}
            el-col(:span="6")
                el-card(shadow="hover")
                    .stat-card
                        .stat-card__number {{ stats.totalGuests }}
                        .stat-card__label {{ $t('Guests') }}

        //- Filters
        el-card.reservations-list__filters(shadow="never" :class="{ 'reservations-list__filters--compact': embedded }")
            el-row(:gutter="embedded ? 10 : 20" type="flex" align="middle")
                el-col(:span="embedded ? 12 : 8")
                    .reservations-list__date-filter
                        el-tooltip(:content="$t('Previous day')" placement="top")
                            el-button(
                                icon="el-icon-arrow-left"
                                :size="embedded ? 'small' : 'medium'"
                                @click="shiftFilterDate(-1)"
                            )
                        .reservations-list__date-field
                            el-date-picker(
                                v-model="filterDate"
                                type="date"
                                :placeholder="$t('Select date')"
                                format="dd.MM.yyyy"
                                value-format="yyyy-MM-dd"
                                :clearable="false"
                                :size="embedded ? 'small' : 'medium'"
                            )
                            el-button.reservations-list__today(
                                v-if="!isFilterDateToday"
                                type="primary"
                                size="mini"
                                @click="goToToday"
                            ) {{ $t('Today') }}
                        el-tooltip(:content="$t('Next day')" placement="top")
                            el-button(
                                icon="el-icon-arrow-right"
                                :size="embedded ? 'small' : 'medium'"
                                @click="shiftFilterDate(1)"
                            )
                el-col(:span="embedded ? 12 : 8")
                    el-select(
                        v-model="filterStatus"
                        :placeholder="$t('All statuses')"
                        clearable
                        style="width: 100%"
                        :size="embedded ? 'small' : 'medium'"
                    )
                        el-option(
                            v-for="(label, key) in statusLabels"
                            :key="key"
                            :label="label"
                            :value="key"
                        )
                el-col(:span="8" style="text-align: right" v-if="!embedded")
                    el-button(
                        v-if="canCreateReservation"
                        type="primary"
                        icon="el-icon-plus"
                        @click="createReservation"
                    ) {{ $t('New Reservation') }}

        //- Reservations table
        el-card.reservations-list__table(shadow="never" :class="{ 'reservations-list__table--compact': embedded }" :style="embedded ? 'margin-top: 10px' : 'margin-top: 20px'")
            el-table(
                ref="reservationTable"
                :data="reservations"
                stripe
                :row-class-name="getRowClassName"
                @row-click="editReservation"
                style="width: 100%; cursor: pointer"
                empty-text="Keine Reservierungen für diesen Tag"
                :size="embedded ? 'mini' : 'medium'"
            )
                el-table-column(
                    prop="time"
                    :label="$t('Time')"
                    width="80"
                )
                    template(slot-scope="scope")
                        strong {{ scope.row.time }}

                el-table-column(
                    prop="guestName"
                    :label="$t('Guest')"
                    min-width="120"
                )
                    template(slot-scope="scope")
                        .reservation-row-draggable(
                            :class="{ 'reservation-row-draggable--enabled': isReservationDraggable(scope.row) }"
                            :draggable="isReservationDraggable(scope.row)"
                            @dragstart.stop="onReservationDragStart(scope.row, $event)"
                            @dragend.stop="onReservationDragEnd($event)"
                        )
                            div {{ scope.row.guestName }}
                            .text-muted(v-if="scope.row.guestPhone && !embedded") {{ scope.row.guestPhone }}

                el-table-column(
                    prop="partySize"
                    :label="embedded ? '#' : $t('Guests')"
                    :width="embedded ? 50 : 70"
                    align="center"
                )
                    template(slot-scope="scope")
                        i.el-icon-fa-user.fas(v-if="!embedded" style="margin-right: 4px")
                        | {{ scope.row.partySize }}

                el-table-column(
                    :label="$t('Table')"
                    :width="embedded ? 70 : 140"
                )
                    template(slot-scope="scope")
                        span(v-if="getTableNames(scope.row.tableIds)") {{ getTableNames(scope.row.tableIds) }}
                        el-tooltip(v-else :content="$t('Not assigned - drag to table')" placement="top")
                            span.unassigned-indicator
                                i.el-icon-warning-outline
                                |  {{ embedded ? '' : $t('Not assigned') }}

                el-table-column(
                    v-if="!embedded"
                    :label="$t('Duration')"
                    width="100"
                    align="center"
                )
                    template(slot-scope="scope")
                        | {{ formatDuration(scope.row.duration) }}

                el-table-column(
                    prop="status"
                    :label="$t('Status')"
                    :width="embedded ? 100 : 130"
                )
                    template(slot-scope="scope")
                        el-tag(
                            :type="getStatusColor(scope.row.status)"
                            size="mini"
                            effect="plain"
                        ) {{ getStatusLabel(scope.row.status) }}

                el-table-column(
                    v-if="!embedded"
                    :label="$t('Notes')"
                    min-width="150"
                )
                    template(slot-scope="scope")
                        .text-muted.notes-truncate {{ scope.row.notes }}

                el-table-column(
                    :label="$t('Actions')"
                    :width="embedded ? 90 : 200"
                    align="right"
                )
                    template(slot-scope="scope")
                        .actions-inline(v-if="embedded")
                            el-tooltip(v-if="scope.row.status === 'pending' && canUpdateReservation" :content="$t('Confirm')" placement="top")
                                el-button(
                                    size="mini"
                                    type="success"
                                    icon="el-icon-check"
                                    circle
                                    @click.stop="updateStatus(scope.row.id, 'confirmed')"
                                )
                            el-tooltip(v-if="scope.row.status === 'confirmed' && canUpdateReservation" :content="$t('Seat')" placement="top")
                                el-button(
                                    size="mini"
                                    icon="el-icon-fa-chair fas"
                                    circle
                                    @click.stop="updateStatus(scope.row.id, 'seated')"
                                )
                            el-tooltip(v-if="canUpdateReservation" :content="$t('Edit')" placement="top")
                                el-button(
                                    size="mini"
                                    icon="el-icon-edit"
                                    circle
                                    @click.stop="editReservation(scope.row)"
                                )
                        template(v-else)
                            el-button-group
                                el-tooltip(v-if="scope.row.status === 'pending' && canUpdateReservation" :content="$t('Confirm')" placement="top")
                                    el-button(
                                        size="mini"
                                        type="success"
                                        icon="el-icon-check"
                                        @click.stop="updateStatus(scope.row.id, 'confirmed')"
                                    )
                                el-tooltip(v-if="scope.row.status === 'confirmed' && canUpdateReservation" :content="$t('Seat')" placement="top")
                                    el-button(
                                        size="mini"
                                        icon="el-icon-fa-chair fas"
                                        @click.stop="updateStatus(scope.row.id, 'seated')"
                                    )
                                el-tooltip(v-if="scope.row.status === 'seated' && canUpdateReservation" :content="$t('Complete')" placement="top")
                                    el-button(
                                        size="mini"
                                        type="info"
                                        icon="el-icon-fa-flag-checkered fas"
                                        @click.stop="updateStatus(scope.row.id, 'completed')"
                                    )
                                el-tooltip(v-if="scope.row.status !== 'cancelled' && scope.row.status !== 'completed' && scope.row.status !== 'no_show' && canUpdateReservation" :content="$t('Cancel')" placement="top")
                                    el-button(
                                        size="mini"
                                        type="danger"
                                        icon="el-icon-close"
                                        @click.stop="cancelReservation(scope.row)"
                                    )
                                el-tooltip(v-if="canUpdateReservation" :content="$t('Edit')" placement="top")
                                    el-button(
                                        size="mini"
                                        icon="el-icon-edit"
                                        @click.stop="editReservation(scope.row)"
                                    )
</template>

<i18n>
{
    "de": {
        "Total": "Gesamt",
        "Confirmed": "Bestätigt",
        "Pending": "Ausstehend",
        "Guests": "Gäste",
        "Select date": "Datum wählen",
        "Previous day": "Vorheriger Tag",
        "Next day": "Nächster Tag",
        "Today": "Heute",
        "All statuses": "Alle Status",
        "New Reservation": "Neue Reservierung",
        "Time": "Uhrzeit",
        "Guest": "Gast",
        "Table": "Tisch",
        "Duration": "Dauer",
        "Status": "Status",
        "Notes": "Notizen",
        "Actions": "Aktionen",
        "Not assigned": "Nicht zugewiesen",
        "Confirm": "Bestätigen",
        "Seat": "Platzieren",
        "Complete": "Abschließen",
        "Cancel": "Stornieren",
        "Edit": "Bearbeiten",
        "Cancel reservation?": "Reservierung stornieren?",
        "Cancel reservation": "Reservierung stornieren",
        "Keep reservation": "Reservierung behalten",
        "This action cannot be undone.": "Diese Aktion kann nicht rückgängig gemacht werden.",
        "Not assigned - drag to table": "Nicht zugewiesen - auf Tisch ziehen"
    }
}
</i18n>

<script>
import _ from 'lodash';
import { addDays, format, isValid, parse } from 'date-fns';
import router from '@/router';
import { checkRestrictions } from '@/helpers';
import { useTableReservationStore, useTableReservationFloorPlanStore } from '@/stores';
import { statusLabels, statusColors } from '@/stores/tableReservation';

export default {
    name: 'ReservationsList',
    props: {
        embedded: {
            type: Boolean,
            default: false
        }
    },
    data: function () {
        return {
            statusLabels: statusLabels
        };
    },
    computed: {
        store: function () {
            return useTableReservationStore();
        },
        floorPlanStore: function () {
            return useTableReservationFloorPlanStore();
        },
        reservations: function () {
            return this.store.filteredReservations;
        },
        stats: function () {
            return this.store.currentStats;
        },
        filterDate: {
            get: function () {
                return this.store.filters.date;
            },
            set: function (value) {
                this.store.setFilterDate(value).catch((error) => {
                    this.showActionError(error);
                });
            }
        },
        filterStatus: {
            get: function () {
                return this.store.filters.status;
            },
            set: function (value) {
                this.store.setFilterStatus(value);
            }
        },
        isFilterDateToday: function () {
            return this.filterDate === format(new Date(), 'yyyy-MM-dd');
        },
        canCreateReservation: function () {
            return checkRestrictions(['ex:trsv:reservations'], 'create');
        },
        canUpdateReservation: function () {
            return checkRestrictions(['ex:trsv:reservations'], 'update');
        }
    },
    methods: {
        getRowClassName: function (info) {
            const row = info.row;
            if (!row.tableIds || !row.tableIds.length) {
                return 'reservation-row--unassigned';
            }
            return '';
        },
        isReservationDraggable: function (reservation) {
            return this.embedded
                && this.canUpdateReservation
                && reservation
                && (!reservation.tableIds || !reservation.tableIds.length);
        },
        onReservationDragStart: function (reservation, event) {
            if (!this.isReservationDraggable(reservation)) {
                event.preventDefault();
                return;
            }

            event.dataTransfer.effectAllowed = 'move';
            event.dataTransfer.setData('text/plain', reservation.id);
            event.dataTransfer.setData('application/x-reservation-id', reservation.id);

            const ghost = document.createElement('div');
            ghost.className = 'reservation-drag-ghost';
            ghost.innerHTML = '<strong>' + _.escape(reservation.guestName) + '</strong>'
                + '<span> (' + reservation.partySize + ' Gäste, ' + reservation.time + ')</span>';
            ghost.style.cssText = 'position:fixed;top:' + (event.clientY - 16) + 'px;left:' + (event.clientX + 12) + 'px;background:#409EFF;color:#fff;padding:8px 14px;border-radius:6px;font-size:13px;white-space:nowrap;z-index:99999;pointer-events:none;box-shadow:0 4px 12px rgba(0,0,0,0.2);';
            document.body.appendChild(ghost);
            event.dataTransfer.setDragImage(ghost, -12, 16);

            setTimeout(() => {
                if (ghost.parentNode) ghost.parentNode.removeChild(ghost);
            }, 50);

            if (event.currentTarget) {
                event.currentTarget.classList.add('reservation-row-draggable--dragging');
            }
            this.$emit('drag-start', reservation);
        },
        onReservationDragEnd: function (event) {
            if (event.currentTarget) {
                event.currentTarget.classList.remove('reservation-row-draggable--dragging');
            }
            this.$emit('drag-end');
        },
        getTableNames: function (tableIds) {
            if (!tableIds || !tableIds.length) return '';
            return tableIds.map((id) => {
                const table = this.floorPlanStore.tables.find((t) => { return t.id === id; });
                return table ? table.name : id;
            }).join(', ');
        },
        goToToday: function () {
            this.filterDate = format(new Date(), 'yyyy-MM-dd');
        },
        shiftFilterDate: function (days) {
            const currentDate = parse(this.filterDate, 'yyyy-MM-dd', new Date());
            const baseDate = isValid(currentDate) ? currentDate : new Date();

            this.filterDate = format(addDays(baseDate, days), 'yyyy-MM-dd');
        },
        getStatusLabel: function (status) {
            return statusLabels[status] || status;
        },
        getStatusColor: function (status) {
            return statusColors[status] || '';
        },
        showActionError: function (error) {
            this.$message({
                message: error.message || this.$t('Something went wrong'),
                type: 'error',
                duration: 5000
            });
        },
        formatDuration: function (minutes) {
            if (!minutes) return '';
            const h = Math.floor(minutes / 60);
            const m = minutes % 60;
            if (h && m) return h + ' Std ' + m + ' Min';
            if (h) return h + (h === 1 ? ' Stunde' : ' Stunden');
            return m + ' Min';
        },
        createReservation: function () {
            router.push({ name: 'table-reservation-form' });
        },
        editReservation: function (row) {
            if (!this.canUpdateReservation) return;
            router.push({ name: 'table-reservation-form', params: { id: row.id } });
        },
        updateStatus: function (id, status) {
            this.store.updateStatus(id, status).catch((error) => {
                this.showActionError(error);
            });
        },
        cancelReservation: function (row) {
            this.$confirm(
                this.$t('This action cannot be undone.'),
                this.$t('Cancel reservation?'),
                {
                    confirmButtonText: this.$t('Cancel reservation'),
                    cancelButtonText: this.$t('Keep reservation'),
                    type: 'warning'
                }
            ).then(() => {
                this.store.updateStatus(row.id, 'cancelled').catch((error) => {
                    this.showActionError(error);
                });
            }).catch(() => {});
        }
    }
};
</script>

<style lang="sass" scoped>
.reservations-list__date-filter
    display: flex
    align-items: center
    gap: 6px

    > .el-tooltip,
    .el-button
        flex: 0 0 auto

.reservations-list__date-field
    position: relative
    flex: 1 1 auto
    min-width: 0

    &::v-deep .el-date-editor
        width: 100%

    // Room for the button inside the field
    &::v-deep .el-input__inner
        padding-right: 62px

.reservations-list__today
    position: absolute
    top: 50%
    right: 5px
    transform: translateY(-50%)
    padding: 4px 8px
    z-index: 2

.stat-card
    text-align: center
    padding: 5px 0
    &__number
        font-size: 2rem
        font-weight: 700
        line-height: 1.2
    &__label
        font-size: 0.85rem
        color: #909399
        margin-top: 4px
    .text-success
        color: #67c23a
    .text-warning
        color: #e6a23c

.stats-compact
    display: flex
    gap: 8px
    &__item
        flex: 1
        background: #f5f7fa
        border-radius: 4px
        padding: 6px 8px
        text-align: center
        border: 1px solid #ebeef5
    &__number
        font-size: 1.1rem
        font-weight: 700
        display: block
        line-height: 1.2
    &__label
        font-size: 0.7rem
        color: #909399
        display: block
        margin-top: 1px
    .text-success
        color: #67c23a
    .text-warning
        color: #e6a23c

.text-muted
    color: #909399
    font-size: 0.85rem

.notes-truncate
    white-space: nowrap
    overflow: hidden
    text-overflow: ellipsis
    max-width: 200px

.actions-inline
    display: flex
    gap: 4px
    justify-content: flex-end

.reservations-list
    &__filters--compact
        &::v-deep .el-card__body
            padding: 10px
    &__table--compact
        &::v-deep .el-card__body
            padding: 0
    &::v-deep .reservation-row--unassigned
        cursor: grab
        background-color: #fff8f0 !important
        &:hover
            background-color: #fff3e0 !important

.reservation-row-draggable
    display: block

    &--enabled
        cursor: grab

    &--dragging
        opacity: 0.4

.unassigned-indicator
    color: #e6a23c
    font-size: 0.85rem
    i
        margin-right: 2px
</style>
